import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Switch,
  TextField,
  Button,
  Grid,
  Divider,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  SmartToy as RobotIcon,
  Save as SaveIcon,
  InfoOutlined as InfoIcon,
  Bolt as BoltIcon,
  BugReport as DebugIcon,
  AutoGraph as AutoIcon,
  Psychology as PsychologyIcon,
  Lightbulb as LightbulbIcon,
} from "@mui/icons-material";
import { useSettings } from "../../context/SettingsContext";
import { getStatusStyles } from "../../config/leadStatus";

const GradeCard = ({ status, description }) => {
  const styles = getStatusStyles(status);
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: "12px",
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        bgcolor: "#f8fafc",
        border: "1px solid #e2e8f0",
      }}
    >
      <Chip
        icon={styles.icon}
        label={styles.label}
        size="small"
        sx={{
          bgcolor: styles.bgcolor,
          color: styles.color,
          fontWeight: 700,
          borderRadius: "6px",
          width: "fit-content",
        }}
      />
      <Typography sx={{ fontSize: 12, color: "#64748b", fontWeight: 500, lineHeight: 1.5 }}>
        {description}
      </Typography>
    </Paper>
  );
};

export default function AIChatMasterControl() {
  const theme = useTheme();
  const { ai_config, updateAiConfig, ready } = useSettings();
  const [localConfig, setLocalConfig] = useState(ai_config);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (ready && ai_config) {
      setLocalConfig(ai_config);
    }
  }, [ai_config, ready]);

  if (!ready) {
    return (
      <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f8fafc" }}>
        <CircularProgress size={40} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  const handleSave = async () => {
    try {
      setSaving(true);
      setSuccess(false);
      await updateAiConfig(localConfig);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error("Failed to save AI config:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (e) => {
    setLocalConfig((p) => ({ ...p, auto_segregation_enabled: e.target.checked }));
  };

  const handleChange = (field) => (e) => {
    setLocalConfig((p) => ({ ...p, [field]: e.target.value }));
  };

  const loadQualificationDefaults = () => {
    setLocalConfig((p) => ({
      ...p,
      qualification_criteria: "Hot Lead (High Intent):\n- Asks about pricing or packages\n- Requests a quote or proposal\n- Wants to book a call or meeting\n\nWarm Lead (Interested):\n- Asks about specific services\n- Shows general interest in growing their business\n\nCold Lead (Low Intent):\n- Just says 'Hi' or 'Hello' with no follow-up\n\nDropped (No Intent):\n- Spam, wrong number, or 'not interested'",
    }));
  };

  const loadPipelineDefaults = () => {
    setLocalConfig((p) => ({
      ...p,
      pipeline_conversion_criteria: "Convert to 'Qualified Leads' (pipeline: qualified) if:\n- User asks for specific service prices (SEO, Ads, Web)\n- User provides their email or city for a quote\n- User requests a callback or a formal meeting\n- User says 'I want to start' or 'How do I sign up?'",
    }));
  };

  const loadPreConsultationDefaults = () => {
    setLocalConfig((p) => ({
      ...p,
      pre_consultation_rules: "Rules for moving from 'Qualified Leads':\n\nTo 'Need Analysis':\n- User asks detailed questions about methodology\n- User requests case studies or portfolio\n- User shares specific business problems to solve\n\nTo 'Booked':\n- User confirms a specific time for consultation\n- User accepts the meeting invite\n- User says 'Yes, I am available at [Time]'\n\nTo 'Dropped':\n- User stops responding after qualification\n- User implies budget mismatch\n- User says 'I will get back to you' and ghosts",
    }));
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      bgcolor: "#f8fafc",
      fontSize: "0.875rem",
    },
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", bgcolor: "#f8fafc" }}>
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          bgcolor: "#fff",
          borderBottom: "1px solid #e2e8f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#0f172a" }}>
            AI Master Control
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#64748b" }}>
            Configure Gemini AI for automated WhatsApp lead segregation & grading
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon sx={{ fontSize: 18 }} />}
          onClick={handleSave}
          disabled={saving}
          disableElevation
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            fontSize: 14,
            px: 3,
            py: 1,
            bgcolor: theme.palette.primary.main,
            "&:hover": { bgcolor: theme.palette.primary.dark },
          }}
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </Box>

      {/* Content */}
      <Box sx={{ p: 3 }}>
        {success && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: "12px" }}>
            AI Configurations updated successfully! Changes are live for new chats.
          </Alert>
        )}

        {/* Two-column Rules */}
        <Grid container spacing={3}>
          {/* Lead Qualification Rules */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: "16px", border: "1px solid #e2e8f0", height: "100%", boxSizing: "border-box" }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <RobotIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Lead Qualification Rules</Typography>
                </Stack>
                <Button size="small" onClick={loadQualificationDefaults} sx={{ textTransform: "none", color: theme.palette.primary.main, fontWeight: 600 }}>
                  Load Defaults
                </Button>
              </Stack>
              <Typography sx={{ fontSize: 13, color: "#64748b", mb: 2 }}>
                Define exactly when a WhatsApp chat should be converted into a Lead.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={10}
                value={localConfig.qualification_criteria}
                onChange={handleChange("qualification_criteria")}
                placeholder="e.g., If they ask about SEO or pricing, convert to lead."
                sx={textFieldSx}
              />
              <Box sx={{ mt: 2, bgcolor: "#EFF6FF", borderRadius: "10px", px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: "#DBEAFE", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <InfoIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                </Box>
                <Typography sx={{ fontSize: 12.5, color: "#475569", lineHeight: 1.5 }}>
                  The AI uses these rules to identify leads and assign grades automatically.
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Pipeline Conversion Rules */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: "16px", border: "1px solid #e2e8f0", height: "100%", boxSizing: "border-box" }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <AutoIcon sx={{ color: "#059669", fontSize: 22 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Pipeline Conversion Rules</Typography>
                </Stack>
                <Button size="small" onClick={loadPipelineDefaults} sx={{ textTransform: "none", color: "#059669", fontWeight: 600 }}>
                  Load Defaults
                </Button>
              </Stack>
              <Typography sx={{ fontSize: 13, color: "#64748b", mb: 2 }}>
                Define when a lead should be automatically converted to the <b>Qualified</b> pipeline stage.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={10}
                value={localConfig.pipeline_conversion_criteria}
                onChange={handleChange("pipeline_conversion_criteria")}
                placeholder="e.g., If they share their email or ask for a quote, convert to qualified."
                sx={textFieldSx}
              />
              <Box sx={{ mt: 2, bgcolor: "#ECFDF5", borderRadius: "10px", px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: "#D1FAE5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <AutoIcon sx={{ fontSize: 16, color: "#059669" }} />
                </Box>
                <Typography sx={{ fontSize: 12.5, color: "#475569", lineHeight: 1.5 }}>
                  When triggered, the lead status will change from a Grade (Hot/Warm/Cold) to Pipeline (Qualified).
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Pre-Consultation Rules */}
        <Paper elevation={0} sx={{ p: 3, borderRadius: "16px", border: "1px solid #e2e8f0", mt: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <PsychologyIcon sx={{ color: "#7c3aed", fontSize: 22 }} />
              <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Pre-Consultation Qualification Rules</Typography>
            </Stack>
            <Button size="small" onClick={loadPreConsultationDefaults} sx={{ textTransform: "none", color: "#7c3aed", fontWeight: 600 }}>
              Load Defaults
            </Button>
          </Stack>
          <Typography sx={{ fontSize: 13, color: "#64748b", mb: 2 }}>
            Define rules for moving qualified leads to <b>Need Analysis</b>, <b>Booked</b>, or <b>Dropped</b>.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            value={localConfig.pre_consultation_rules || ""}
            onChange={handleChange("pre_consultation_rules")}
            placeholder="e.g., Move to 'Booked' if they confirm a time slot."
            sx={textFieldSx}
          />
          <Box sx={{ mt: 2, bgcolor: "#F5F3FF", borderRadius: "10px", px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: "#EDE9FE", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <InfoIcon sx={{ fontSize: 16, color: "#7c3aed" }} />
            </Box>
            <Typography sx={{ fontSize: 12.5, color: "#475569", lineHeight: 1.5 }}>
              AI will verify these conditions before moving leads further down the pipeline.
            </Typography>
          </Box>
        </Paper>

        {/* AI Status Bar */}
        <Paper elevation={0} sx={{ p: 3, borderRadius: "16px", border: "1px solid #e2e8f0", mt: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <BoltIcon sx={{ color: "#f59e0b", fontSize: 24 }} />
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 16 }}>AI Status</Typography>
                <Typography sx={{ fontSize: 12, color: "#64748b" }}>Manage global AI behavior settings</Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={3} alignItems="center">
              <Box>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#64748b", letterSpacing: 1, mb: 0.5 }}>
                  CONFIDENCE THRESHOLD
                </Typography>
                <TextField
                  type="number"
                  size="small"
                  value={localConfig.lead_creation_threshold}
                  onChange={handleChange("lead_creation_threshold")}
                  inputProps={{ step: 0.1, min: 0, max: 1 }}
                  sx={{ width: 80, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                />
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Switch
                  checked={localConfig.auto_segregation_enabled}
                  onChange={handleToggle}
                  color="success"
                  size="small"
                />
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: localConfig.auto_segregation_enabled ? "#059669" : "#94a3b8" }}>
                  {localConfig.auto_segregation_enabled ? "Active" : "Inactive"}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Paper>

        {/* Grading Definitions */}
        <Paper elevation={0} sx={{ p: 3, borderRadius: "16px", border: "1px solid #e2e8f0", mt: 3, overflow: "hidden" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 2.5 }}>Grading Definitions</Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: "1 1 calc(25% - 12px)", minWidth: 180 }}>
              <GradeCard status="hot" description="Direct inquiry for specific service/price. High urgency and clear intent." />
            </Box>
            <Box sx={{ flex: "1 1 calc(25% - 12px)", minWidth: 180 }}>
              <GradeCard status="warm" description="Interested but needs more info/details. Exploring options." />
            </Box>
            <Box sx={{ flex: "1 1 calc(25% - 12px)", minWidth: 180 }}>
              <GradeCard status="cold" description="General 'Hi' or non-specific inquiry. No clear business intent shown yet." />
            </Box>
            <Box sx={{ flex: "1 1 calc(25% - 12px)", minWidth: 180 }}>
              <GradeCard status="dropped" description="Spam, wrong number, or no interest. System ignores these contacts." />
            </Box>
          </Box>
        </Paper>

        {/* Pro Tip Banner */}
        <Paper
          sx={{
            p: 3,
            borderRadius: "16px",
            mt: 3,
            bgcolor: theme.palette.primary.main,
            color: "#fff",
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <LightbulbIcon sx={{ fontSize: 22, mt: 0.25 }} />
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 0.5 }}>Pro Tip</Typography>
              <Typography sx={{ fontSize: 13, opacity: 0.92, lineHeight: 1.6 }}>
                Use the prompt to teach the AI about your specific services (SEO, Social Media, etc.) so it can extract{" "}
                <Box component="span" sx={{ fontFamily: "monospace", bgcolor: "rgba(255,255,255,0.15)", px: 0.75, py: 0.25, borderRadius: 1, fontSize: 12 }}>
                  "services_interested"
                </Box>{" "}
                accurately from conversations.
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* AI Segregation Monitor */}
        <Paper elevation={0} sx={{ p: 3, borderRadius: "16px", border: "1px solid #e2e8f0", mt: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
            <DebugIcon sx={{ color: "#ef4444", fontSize: 22 }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 16 }}>AI Segregation Monitor</Typography>
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#64748b", letterSpacing: 1 }}>
                REAL-TIME ACTIVITY
              </Typography>
            </Box>
            <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#22c55e" }} />
          </Stack>
          <Divider sx={{ mb: 3 }} />
          <Box sx={{ py: 5, textAlign: "center" }}>
            <AutoIcon sx={{ fontSize: 40, color: "#e2e8f0", mb: 1 }} />
            <Typography sx={{ fontSize: 14, color: "#94a3b8", mb: 0.5 }}>
              Live segregation events will appear here as they are processed.
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#cbd5e1" }}>
              Connecting to WhatsApp stream...
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
